import { useEffect, useState } from 'react'
import { createClient } from '@/lib/supabase/client'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { MessageCircle, Search, User, Bot, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface Message {
  id: number
  session_id: string
  message: {
    type: 'human' | 'ai'
    content: string
    tool_calls?: any[]
    additional_kwargs?: any
  }
}

interface Conversation {
  session_id: string
  messages: Message[]
  last_message?: string
  message_count: number
}

export default function ConversasPalatiumPage() {
  const supabase = createClient()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedSession, setSelectedSession] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    fetchConversations()
  }, [])

  useEffect(() => {
    if (selectedSession) {
      fetchMessages(selectedSession)
    }
  }, [selectedSession])

  const fetchConversations = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('n8n_chat_histories_palatium')
        .select('*')
        .order('id', { ascending: true })

      if (error) {
        console.error('Erro ao buscar conversas:', error)
        return
      }

      // Agrupar mensagens por session_id
      const grouped: Record<string, Message[]> = {}
      data.forEach((msg: any) => {
        if (!grouped[msg.session_id]) {
          grouped[msg.session_id] = []
        }
        grouped[msg.session_id].push(msg)
      })

      // Criar array de conversas
      const conversationsList: Conversation[] = Object.keys(grouped).map(sessionId => {
        const sessionMessages = grouped[sessionId].sort((a, b) => a.id - b.id)
        const lastMessage = sessionMessages[sessionMessages.length - 1]
        const lastMessageContent = lastMessage?.message?.content || ''
        
        return {
          session_id: sessionId,
          messages: sessionMessages,
          last_message: lastMessageContent.length > 80 
            ? lastMessageContent.substring(0, 80) + '...' 
            : lastMessageContent,
          message_count: sessionMessages.length
        }
      })

      // Ordenar por última mensagem (mais recentes primeiro)
      conversationsList.sort((a, b) => {
        const aLastId = a.messages[a.messages.length - 1]?.id || 0
        const bLastId = b.messages[b.messages.length - 1]?.id || 0
        return bLastId - aLastId
      })

      setConversations(conversationsList)
      
      // Selecionar a primeira conversa automaticamente
      if (conversationsList.length > 0 && !selectedSession) {
        setSelectedSession(conversationsList[0].session_id)
      }
    } catch (error) {
      console.error('Erro ao buscar conversas:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchMessages = async (sessionId: string) => {
    try {
      const { data, error } = await supabase
        .from('n8n_chat_histories_palatium')
        .select('*')
        .eq('session_id', sessionId)
        .order('id', { ascending: true })

      if (error) {
        console.error('Erro ao buscar mensagens:', error)
        return
      }

      setMessages(data || [])
    } catch (error) {
      console.error('Erro ao buscar mensagens:', error)
    }
  }

  const filteredConversations = conversations.filter(conv => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      conv.session_id.toLowerCase().includes(searchLower) ||
      conv.last_message?.toLowerCase().includes(searchLower) ||
      conv.messages.some(msg => 
        msg.message.content?.toLowerCase().includes(searchLower)
      )
    )
  })

  const selectedConversation = conversations.find(c => c.session_id === selectedSession)

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">Conversas WhatsApp - Palatium</h1>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="secondary">
                {conversations.length} {conversations.length === 1 ? 'conversa' : 'conversas'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchConversations()}
                disabled={loading}
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
                Atualizar
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 h-[calc(100vh-73px)]">
        <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4 h-full">
          {/* Lista de Conversas */}
          <Card className="flex flex-col h-full">
            <CardHeader className="pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar conversas..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </CardHeader>
            <CardContent className="flex-1 p-0 overflow-hidden">
              <ScrollArea className="h-full">
                <div>
                  {loading ? (
                    <div className="p-4 text-center text-muted-foreground">
                      Carregando conversas...
                    </div>
                  ) : filteredConversations.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      Nenhuma conversa encontrada
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredConversations.map((conv) => (
                        <button
                          key={conv.session_id}
                          onClick={() => setSelectedSession(conv.session_id)}
                          className={cn(
                            "w-full p-4 text-left hover:bg-muted/50 transition-colors",
                            selectedSession === conv.session_id && "bg-primary/10 border-l-4 border-l-primary"
                          )}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <MessageCircle className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium text-sm">
                                {conv.session_id.length > 15 
                                  ? `${conv.session_id.substring(0, 15)}...` 
                                  : conv.session_id}
                              </span>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {conv.message_count}
                            </Badge>
                          </div>
                          {conv.last_message && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {conv.last_message}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Área de Mensagens */}
          <Card className="flex flex-col h-full">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                {selectedConversation ? (
                  <span className="text-sm font-normal text-muted-foreground">
                    Conversa: {selectedConversation.session_id}
                  </span>
                ) : (
                  <span className="text-sm font-normal text-muted-foreground">
                    Selecione uma conversa
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 p-0 overflow-hidden">
              {selectedConversation ? (
                <ScrollArea className="h-full">
                  <div className="p-4 space-y-4">
                    {messages.map((msg) => {
                      const isHuman = msg.message.type === 'human'
                      return (
                        <div
                          key={msg.id}
                          className={cn(
                            "flex gap-3",
                            isHuman ? "justify-end" : "justify-start"
                          )}
                        >
                          {!isHuman && (
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <Bot className="h-4 w-4 text-primary" />
                            </div>
                          )}
                          <div
                            className={cn(
                              "rounded-lg p-3 max-w-[80%]",
                              isHuman
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted"
                            )}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {isHuman ? (
                                <User className="h-3 w-3" />
                              ) : (
                                <Bot className="h-3 w-3 text-primary" />
                              )}
                              <span className="text-xs font-medium">
                                {isHuman ? 'Cliente' : 'Assistente IA'}
                              </span>
                              <span className="text-xs opacity-70">
                                #{msg.id}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap break-words">
                              {msg.message.content || '(Sem conteúdo)'}
                            </p>
                            {msg.message.tool_calls && msg.message.tool_calls.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-current/20">
                                <p className="text-xs opacity-70">
                                  {msg.message.tool_calls.length} ferramenta(s) utilizada(s)
                                </p>
                              </div>
                            )}
                          </div>
                          {isHuman && (
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <User className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <div className="text-center">
                    <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Selecione uma conversa para visualizar</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

